import React, { useMemo } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { UiModificationsErrorHandler } from '@atlassian/ui-modifications-core';
import { ISSUE_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { AnalyticsListener, getErrorType } from '@atlassian/ui-modifications-analytics';
import { getAnalyticsWebClientPromise } from '@atlassian/jira-product-analytics-web-client-async';
import { UiModificationsJiraRoot } from '@atlassian/jira-ui-modifications-common/src/common/ui/jira-root/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { JiraIssueTypeAri, JiraProjectAri } from '@atlassian/ari/jira';
import { GIC_VIEW_TYPE } from '../../common/constants/index.tsx';
import type { Props } from './types.tsx';
import { getSupportedFieldsCount } from './utils/get-supported-fields-count/index.tsx';

const UiModificationsEntryPointLoaded = ({
	issueTypeFieldType,
	modalFieldConfig,
	resetFormFieldsCounter,
	selectedIssueType,
	selectedProject,
	triggerPointKey,
}: Props) => {
	const cloudId = useCloudId();
	const executionContext = useMemo(() => {
		if (selectedProject && selectedIssueType) {
			return {
				project: {
					id: selectedProject.value,
					key: selectedProject.projectKey,
					type: selectedProject.projectType,
				},
				issueType: {
					id: selectedIssueType.value,
					name: selectedIssueType.label,
				},
				viewType: GIC_VIEW_TYPE,
			};
		}

		return null;
	}, [selectedIssueType, selectedProject]);

	const fieldsCount = useMemo(
		() =>
			getSupportedFieldsCount({
				...modalFieldConfig,
				[ISSUE_TYPE]: {
					fieldType: issueTypeFieldType,
				},
			}),

		[issueTypeFieldType, modalFieldConfig],
	);

	const queryParams = useMemo(() => {
		if (!executionContext?.project.id || !executionContext?.issueType.id) {
			return null;
		}

		return {
			cloudId,
			forgeExtensionRenderingContext: {
				projectKey: executionContext?.project.key,
			},
			uiModificationsContext: {
				projectId: JiraProjectAri.create({
					siteId: cloudId,
					projectId: executionContext?.project.id,
				}).toString(),
				issueTypeId: JiraIssueTypeAri.create({
					siteId: cloudId,
					issueTypeId: executionContext?.issueType.id,
				}).toString(),
				viewType: GIC_VIEW_TYPE,
			},
			includeUserAccessEnabledForge: fg('forge_query_include_user_access_enabled'),
		};
	}, [
		cloudId,
		executionContext?.issueType.id,
		executionContext?.project.id,
		executionContext?.project.key,
	]);

	const executionContextKey = useMemo(
		() =>
			selectedIssueType?.value && selectedProject?.value
				? `${selectedProject.value}-${selectedIssueType.value}`
				: null,
		[selectedIssueType?.value, selectedProject?.value],
	);

	if (!executionContext || !queryParams) {
		return null;
	}

	return (
		<UiModificationsJiraRoot
			containerId="uiModifications"
			containerPackageName="jiraUiModificationsViewGic"
			queryParams={queryParams}
			resetFormFieldsCounter={resetFormFieldsCounter}
			executionContextKey={executionContextKey}
			executionContext={executionContext}
			numberOfSupportedFields={fieldsCount}
			triggerPointKey={triggerPointKey}
		/>
	);
};

export const UiModificationsEntryPoint = (props: Props) => (
	<JSErrorBoundary
		id="uiModifications"
		packageName="jiraUiModificationsViewGic"
		fallback={() => <UiModificationsErrorHandler />}
		attributes={(error: Error) => ({
			errorType: getErrorType(error),
			viewType: GIC_VIEW_TYPE,
		})}
		sendToPrivacyUnsafeSplunk
	>
		<AnalyticsListener
			analyticsWebClient={getAnalyticsWebClientPromise().then((client) => client.getInstance())}
		>
			<UiModificationsEntryPointLoaded {...props} />
		</AnalyticsListener>
	</JSErrorBoundary>
);
